import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import { ModelUpdatingResponse, AddReactionsData } from "./types"
import { userIdsSelector } from "../recoil/atoms/users"
import {
  activeSpeakerSelector,
  activeSpeakerCallbackSelector
} from "../recoil/atoms/activeSpeaker"
import { ConsoleLogger } from "../util/logger"
import { roomSelector } from "../recoil/atoms/room"
import {
  chatIdsSelector,
  chatIsReadAtom,
  isClearChatAtom,
  chatCallbackSelector,
  isFirstTimeChatResponseCallbackSelector,
  isFirstTimeChatResponseAtom,
  replyIdsCallbackSelector,
  lastReactionAtom
} from "../recoil/atoms/chats"
import { watchPartyHostIdSelector } from "../recoil/atoms/watchPartyScreen"
import {
  connectBotStreamAtom,
  streamsAtom,
  middlewareStream,
  streamsLoaderCallbackSelector
} from "../recoil/atoms/streams"
import * as Util from "../util"
import { heartAnimationSelector } from "../recoil/atoms/heartAnimation"
import {
  connectBotRecordingAtom,
  recordingLoadeCallbackSelector
} from "../recoil/atoms/recording"
import { auctionParamsSelector } from "../recoil/atoms/auction"
import { badgeIdsSelector } from "../recoil/atoms/badges"
import { auctionFinalResultSelector } from "../recoil/atoms/auction"
import { watchPartyHistoryLinksSelector } from "../recoil/atoms/watchPartyHistoryLinks"
import { badgeHisrotyIdsSelector } from "../recoil/atoms/badgeHistory"
import { pollsSelector, pollsCallbackSelector } from "../recoil/atoms/polls"
import { overlaySelector, showBadgeOverlayAtom } from "../recoil/atoms/overlay"
import { pipParamsSelector } from "../recoil/atoms/pictureInPicture"
import { localUserCallbackSelector } from "../recoil/atoms/users"
import { RECORDING_USER_ID } from "../constants"
import useServerWatchParty from "./useServerWatchParty"
import { useHeartAnimation } from "../hooks/useHeartAnimation"
import { notificationNewUsersSelector } from "../recoil/atoms/notification"
import { audienceTotalCountSelector } from "../recoil/atoms/userCounts"
import { shoppableVideosListSelector } from "../recoil/atoms/shoppableVideos"
import {
  shoppableMNPVideoCallbackSelector,
  shoppableVideosListCallbackSelector,
  shoppableMNPVideoAtom,
  shoppableVideosAfterPictureNoPresentCallbackSelector,
  shoppableVideosAfterPictureNoPresentAtom
} from "../recoil/atoms/shoppableVideos"
import { taggedBadgesAtom } from "../recoil/atoms/taggedBadges"
import { parsePollOption, sortPollOptions } from "../util/poll-util"
import { screenShareUserSelector } from "../recoil/atoms/screenShareUser"
import useHighQualityStreamSettings from "../hooks/useHighQualityStreamSettings"
import { useRef } from "react"
import useRoomDurationTimeout from "../hooks/useRoomDurationTimeout"
import {
  whiteboardRoomDetailsAtom, whiteboardShareUserAtom,

} from "../recoil/atoms/whiteboard"
import { fileConversionsAtom } from "../recoil/atoms/fileConversion"

interface ModalUpdateProps {
  doUserUpdate: (resp: ModelUpdatingResponse) => Promise<void>
  doRoomUpdate: (response: ModelUpdatingResponse) => void
  doChatUpdate: (response: ModelUpdatingResponse) => void
  doOverlayUpdate: (response: ModelUpdatingResponse) => void
  doProductsUpdate: (response: ModelUpdatingResponse) => void
  doPollUpdate: (response: ModelUpdatingResponse) => void
  doStreamConfigUpdate: (response: ModelUpdatingResponse) => void
  doAuctionUpdate: (response: ModelUpdatingResponse) => void
  doWatchPartyConfigUpdate: (response: ModelUpdatingResponse) => void
  doMediaUpdate: (response: ModelUpdatingResponse) => void
}

const logger = new ConsoleLogger()
const UserPermission = window?.MeetOnline?.UserPermission
const UserType = window.MeetOnline?.UserType
const ChatStatus = window.MeetOnline?.ChatStatus

export const useModalUpdate = (): ModalUpdateProps => {
  const setEnabledOverlay = useSetRecoilState(showBadgeOverlayAtom)
  const setRoom = useSetRecoilState(roomSelector)
  const setChats = useSetRecoilState(chatIdsSelector)
  const setShoppableVideos = useSetRecoilState(shoppableVideosListSelector)
  const setConnectBot = useSetRecoilState<boolean>(connectBotStreamAtom)
  const setHeartAnimation = useSetRecoilState(heartAnimationSelector)
  const setAuctionParams = useSetRecoilState(auctionParamsSelector)
  const setBadges = useSetRecoilState(badgeIdsSelector)
  const setPiPParams = useSetRecoilState(pipParamsSelector)
  const setOverlay = useSetRecoilState(overlaySelector)
  const setStreamsList = useSetRecoilState(streamsAtom)
  const setLastReactionsId = useSetRecoilState(lastReactionAtom)
  const setWatchPartyHistoryLinks = useSetRecoilState(
    watchPartyHistoryLinksSelector
  )
  const setNotificationNewUsersParams = useSetRecoilState(
    notificationNewUsersSelector
  )
  const activeSpeakerCallback = useRecoilValue(activeSpeakerCallbackSelector)
  const setChatIsRead = useSetRecoilState<boolean>(chatIsReadAtom)
  const setAuctionFinalResult = useSetRecoilState(auctionFinalResultSelector)
  const setBadgeHistory = useSetRecoilState(badgeHisrotyIdsSelector)
  const setIsClearChat = useSetRecoilState(isClearChatAtom)
  const setPolls = useSetRecoilState(pollsSelector)
  const setConnectRecordingBot = useSetRecoilState<boolean>(
    connectBotRecordingAtom
  )
  const chatCallbackProps = useRecoilValue(chatCallbackSelector)
  const replyIdsCallbackProps = useRecoilValue(replyIdsCallbackSelector)
  const localUserCallbackProps = useRecoilValue(localUserCallbackSelector)
  const isFirstTimeChatResponseProps = useRecoilValue(
    isFirstTimeChatResponseCallbackSelector
  )
  const shoppableVideosListProps = useRecoilValue(
    shoppableVideosListCallbackSelector
  )
  const shoppableVideosAfterPictureNoPresentProps = useRecoilValue(
    shoppableVideosAfterPictureNoPresentCallbackSelector
  )
  const pollsCallbackProps = useRecoilValue(pollsCallbackSelector)
  const setFirstTimeChatResponse = useSetRecoilState(
    isFirstTimeChatResponseAtom
  )
  const streamsLoaderCallBack = useRecoilValue(streamsLoaderCallbackSelector)
  const recordingLoaderCallback = useRecoilValue(recordingLoadeCallbackSelector)
  const shoppableMNPVideoProps = useRecoilValue(
    shoppableMNPVideoCallbackSelector
  )
  const setWatchPartyHostId = useSetRecoilState(watchPartyHostIdSelector)
  const setShoppableVideosAfterPictureNoPresent = useSetRecoilState(
    shoppableVideosAfterPictureNoPresentAtom
  )
  const setShoppableMNPVideo = useSetRecoilState(shoppableMNPVideoAtom)
  const shoppableMNPVideoCallback = useRecoilValue(
    shoppableMNPVideoCallbackSelector
  )
  const setTaggedBadges = useSetRecoilState(taggedBadgesAtom)
  const setAudienceCount = useSetRecoilState(audienceTotalCountSelector)
  const { setAnimationHeart } = useHeartAnimation(false)
  const setUsers = useSetRecoilState(userIdsSelector)
  const setActiveSpeaker = useSetRecoilState(activeSpeakerSelector)
  const setScreenShareUser = useSetRecoilState(screenShareUserSelector)
  const setWhiteboardShareUser = useSetRecoilState(whiteboardShareUserAtom)
  const setWhiteboardRoomDetails = useSetRecoilState(whiteboardRoomDetailsAtom)
  const MeetOnline = window.MeetOnline
  const { startServerWatchParty, stopServerWatchParty } = useServerWatchParty()
  const {
    setAudienceBuffering,
    setStreamingResolution,
    setRtmpStreamEnable,
    setEnableHighQualityStream
  } = useHighQualityStreamSettings()

  const { handleRoomDurationResponse } = useRoomDurationTimeout()

  const [fileConversions, setFileConversions] = useRecoilState(fileConversionsAtom);

  const handleAnimationHeart = colorHeart => {
    setAnimationHeart({
      enabled: true,
      color: colorHeart
    })
  }

  const stopScreenShareIfPermissionRevoke = async permissions => {
    const localUser = await localUserCallbackProps.handleReturnData()
    if (!permissions) return
    const client = window.MeetOnline.client()

    if (
      !Util.hasPermission(permissions, UserPermission.canUseScreenshare) &&
      localUser?.screenShareEnabled
    ) {
      // turn off the screen share
      client?.rtc.toggleScreenShare(false)
    }
  }

  // TODO: enable when permissions are sorted on PHP  
  const stopWhiteboardShareIfPermissionRevoke = async permissions => {
    const localUser = await localUserCallbackProps.handleReturnData()
    if (!permissions) return
    const client = window.MeetOnline.client()

    if (
      !Util.hasPermission(permissions, UserPermission.canUseWhiteboardShare) &&
      localUser?.whiteboardShareEnabled
    ) {
      // turn off the screen share
      client?.rtc.toggleWhiteboardShare(false)
    }
  }

  const disableLocalDevices = async (permissions: number) => {
    const localUser = await localUserCallbackProps.handleReturnData()
    if (!permissions) return
    const client = window.MeetOnline.client()

    if (
      !Util.hasPermission(permissions, UserPermission.canUseMic) &&
      localUser?.micEnabled
    ) {
      // turn off the microphone
      client?.toggleMic(true, localUser.id)
    }

    if (
      !Util.hasPermission(permissions, UserPermission.canUseCam) &&
      localUser?.camEnabled
    ) {
      // turn off the camera
      client?.toggleCam(true, localUser.id)
    }
    if (
      !Util.hasPermission(permissions, UserPermission.canUseScreenshare) &&
      localUser?.screenShareEnabled
    ) {
      // turn off the screen share and watch party video.
      client?.rtc.toggleScreenShare(false)
      let wpHostId = MeetOnline?.client().rtc?.getWatchPartyHostId()
      if (
        wpHostId !== "modNotPresentImage" &&
        wpHostId !== "modNotPresentVideo" &&
        wpHostId !== "modNotPresentOverlay"
      ) {
        stopServerWatchParty()
      }
    }
  }

  const changeClientRoleToAudience = permissions => {
    // check if current user cam and mic permission are revoked
    if (
      !Util.hasPermission(permissions, UserPermission.canUseMic) &&
      !Util.hasPermission(permissions, UserPermission.canUseCam)
    ) {
      // change set Client role to audience
      MeetOnline?.client().rtc?.setClientRoleToAudience()
    }
  }

  const showInteractivityRequiredVisual = async value => {
    if (value) {
      //  showInteractivityRequiredVisual is true
      // let wpHostId = MeetOnline?.client().rtc?.getWatchPartyHostId()
      // if (wpHostId === "") {
      startServerWatchParty()
      // }
    } else {
      // stop server watch party
      let wpHostId = MeetOnline?.client().rtc?.getWatchPartyHostId()

      const shoppableVideoInfo =
        await shoppableMNPVideoCallback.handleReturnData()

      if (
        wpHostId === "modNotPresentImage" ||
        wpHostId === "modNotPresentVideo" ||
        wpHostId === "modNotPresentOverlay" ||
        (shoppableVideoInfo?.enabledShoppableMNP &&
          shoppableVideoInfo?.typeShoppableMNP !== "share")
      ) {
        stopServerWatchParty()
      }
    }

    setRoom({
      name: "UPDATE",
      attributes: {
        showInteractivityRequiredVisual: value
      }
    })
  }

  const toggleScreenShare = async (value: boolean) => {
    await MeetOnline?.client().rtc.toggleScreenShare(value)
  }
  
  const toggleWhiteboardShare = async (value: boolean) => {
    await MeetOnline?.client().rtc.toggleWhiteboardShare(value)
  }

  const doUserUpdate = async (resp: ModelUpdatingResponse) => {
    logger.log("ModelUpdatingResponse:", resp)
    const localUser = await localUserCallbackProps.handleReturnData()

    // check whether this is a file conversion message
    if (resp.data && resp.data[0] && resp.data[0].status) {
      console.log(resp)
      const updateData = resp.data[0]

      const conversions = fileConversions.conversions.slice()
      const updatedConversionIdx = conversions.findIndex(
        conversion => conversion.uuid == updateData.uuid
      )

      if (updatedConversionIdx != -1) {
        // update existing entry
        conversions[updatedConversionIdx] = {
          ...updateData,
          images:
            updateData.images != null
              ? Object.keys(updateData.images).map(k => updateData.images[k])
              : []
        }
      } else {
        // create a new entry
        const entry = {
          ...updateData,
          images:
            updateData.images != null
              ? Object.keys(updateData.images).map(k => updateData.images[k])
              : [],
          spawnedInWhiteboard: false
        }
        console.log(entry)
        conversions.push(entry)
      }

      setFileConversions({ conversions: conversions })
    }

    if (resp.userCounts) {
      setAudienceCount(resp.userCounts.audienceCount)
    }

    // Add USERS LIST / SINGLE USER to recoil
    if (resp?.action === undefined || resp?.action === "create") {
      setUsers({ name: "ADD", users: resp.data })
      setNotificationNewUsersParams({ isSoundNewUser: true })

      if (resp?.showInteractivityRequiredVisual !== undefined) {
        showInteractivityRequiredVisual(resp?.showInteractivityRequiredVisual)
      }

      const _isStreamsLoader = await streamsLoaderCallBack.handleReturnData()
      const _isRecordingLoader =
        await recordingLoaderCallback.handleReturnData()

      let isRecordingUser = false
      for (let singleUser of resp.data) {
        if (singleUser.id === RECORDING_USER_ID) {
          isRecordingUser = true
          break
        }
      }

      if (isRecordingUser && _isStreamsLoader) {
        setConnectBot(true)
      }

      if (isRecordingUser && _isRecordingLoader) {
        setConnectRecordingBot(true)
      }

      // setUsers({ name: "ADD", users: resp.data }) // Add new Joinee

      //Case when 2nd person joins and nobody speaking. We never want to show Current User as Active Speaker until he is alone in the room
      /* if (_localUserId === _activeSpeaker.uid) {
        setActiveSpeaker({ uid: resp.data.id, type: "remote" })
      } */

      return
    } else if (resp?.action === "update") {
      // Update user properties

      for (let singleUser of resp.data) {
        // Set user id for current user object from list
        let userId = ""
        if (singleUser?.id === undefined) {
          userId = localUser.id
        } else {
          userId = singleUser?.id
        }

        if (
          userId &&
          userId !== "all_broadcaster" &&
          userId !== "all_audience"
        ) {
          // animate the heart for non local user
          if (
            singleUser?.colorHeart !== undefined &&
            localUser?.id !== userId
          ) {
            handleAnimationHeart(singleUser.colorHeart)
          }

          if (
            singleUser.colorHeart === undefined &&
            singleUser.screenShareEnabled === undefined
          ) {
            setUsers({
              name: "UPDATE",
              userId: userId,
              attributes: singleUser
            })
          }

          // if current user permissions are revoked by moderator, disable cam or mic accordingly
          if (
            userId === localUser?.id &&
            singleUser?.permissions !== undefined
          ) {
            if (
              !Util.hasPermission(
                +singleUser?.permissions,
                UserPermission.canUseMic
              )
            ) {
              if (localUser.micEnabled) {
                // mute the mic
                MeetOnline?.client().toggleMic(true, localUser.id)
                await MeetOnline?.client().rtc.muteAudio()
              }
            }

            if (
              !Util.hasPermission(
                +singleUser?.permissions,
                UserPermission.canUseCam
              )
            ) {
              if (localUser.camEnabled) {
                // mute the cam
                MeetOnline?.client().toggleCam(true, localUser.id)
                await MeetOnline?.client().rtc.muteVideo()
              }
            }

            stopScreenShareIfPermissionRevoke(+singleUser?.permissions)
            // stopWhiteboardShareIfPermissionRevoke(+singleUser?.permissions) // TODO: uncomment when adding permissions

            // check if current user cam and mic permission are revoked
            changeClientRoleToAudience(+singleUser?.permissions)
          }


          //toggle mic for local user
          if (singleUser.micEnabled !== undefined && userId === localUser?.id) {
            if (singleUser.micEnabled === true) {
              await MeetOnline?.client().rtc.unmuteAudio()
            } else {
              await MeetOnline?.client().rtc.muteAudio()
            }
          }

          //toggle cam for local user
          if (singleUser.camEnabled !== undefined && userId === localUser?.id) {
            if (singleUser.camEnabled === true) {
              await MeetOnline?.client().rtc.unmuteVideo()
            } else {
              await MeetOnline?.client().rtc.muteVideo()
            }
          }

          //toggle Screenshare for local user
          if (
            singleUser.screenShareEnabled !== undefined &&
            userId === localUser?.id
          ) {
            if (
              localUser?.screenShareEnabled !== singleUser.screenShareEnabled
            ) {
              toggleScreenShare(singleUser.screenShareEnabled)
            }
            stopServerWatchParty()
          }

          if (
            singleUser.screenShareEnabled !== undefined &&
            userId !== localUser?.id
          ) {
            setUsers({
              name: "UPDATE",
              userId: userId,
              attributes: {
                screenShareEnabled: singleUser.screenShareEnabled
              }
            })
            // if false: stop screen-share
            if (singleUser.screenShareEnabled === false) {
              setScreenShareUser(null)
            }

            if (localUser?.screenShareEnabled) {
              // stop current user screenshare if enabled
              toggleScreenShare(false)
            }
          }

          if (
            singleUser.whiteboardShareEnabled !== undefined && userId !== localUser?.id) {
              if (singleUser.whiteboardShareEnabled) {
                console.log("setting whiteboard share user", "ws" + userId, "because another user has started whiteboard share")
                setWhiteboardShareUser({uid:"ws" + userId})
              } else {
                console.log("setting whiteboard share user", null, "because another user has stopped whiteboard share")
                setWhiteboardShareUser(null)
              }
            }

          if (
            singleUser.whiteboardRoomId !== undefined &&
            singleUser.whiteboardRoomToken !== undefined
          ) {
            console.log({
              roomId: singleUser.whiteboardRoomId,
              roomToken: singleUser.whiteboardRoomToken
            })
            setWhiteboardRoomDetails({
              roomId: singleUser.whiteboardRoomId,
              roomToken: singleUser.whiteboardRoomToken
            })
          }

          if (
            singleUser.whiteboardShareEnabled !== undefined &&
            userId === localUser?.id
          ) {
            console.log("whiteboardShareEnabled", singleUser.whiteboardShareEnabled)
            console.log(singleUser)
            if (localUser?.whiteboardShareEnabled !== singleUser.whiteboardShareEnabled) {
              toggleWhiteboardShare(singleUser.whiteboardShareEnabled)
            }
            stopServerWatchParty()
          }

          if (
            singleUser.whiteboardShareEnabled !== undefined &&
            userId !== localUser?.id
          ) {
            console.log("whiteboardShareEnabled", singleUser.whiteboardShareEnabled)
            console.log(singleUser)

            setUsers({
              name: "UPDATE",
              userId: userId,
              attributes: {
                whiteboardShareEnabled: singleUser.whiteboardShareEnabled
              }
            })
            // if false: stop whiteboard-share
            if (singleUser.whiteboardShareEnabled === false) {
              console.log("setting whiteboard share user", null, "because whiteboard sharing was disabled")
              setWhiteboardShareUser(null)
            }

            if (localUser?.whiteboardShareEnabled) {
              // stop current user whiteboard share if enabled
              toggleWhiteboardShare(false)
            }
          }

          if (
            singleUser.usernameChanged !== undefined &&
            userId !== localUser?.id
          ) {
            console.log("usernameChanged", singleUser.usernameChanged)
            console.log(singleUser)

            setUsers({
              name: "UPDATE",
              userId: userId,
              attributes: {
                name: singleUser.usernameChanged
              }
            })
          }

          // TODO: Backend dev said toggleAllPermision is not available in server code
          /* if (resp.data.properties.toggleAllPermision !== undefined) {
            setUsers({
              name: "UPDATE",
              userId: localUser?.id,
              attributes: { permissions: resp.data.properties.permissions }
            })
          } */
        }

        // if (
        //   singleUser?.permissions === undefined &&
        //   resp?.showInteractivityRequiredVisual === true
        // ) {
        //   showInteractivityRequiredVisual(true)
        // // eslint-disable-next-line @typescript-eslint/no-unused-vars
        // let wpHostId = MeetOnline?.client().rtc?.getWatchPartyHostId()
        // // if (wpHostId == '' || wpHostId == "modNotPresentImage" || wpHostId == "modNotPresentVideo" || wpHostId == "modNotPresentOverlay") {
        // // if(wpHostId != "modNotPresentVideo"){
        // startServerWatchParty()
        // // }
        // // }

        // setRoom({
        //   name: "UPDATE",
        //   attributes: {
        //     showInteractivityRequiredVisual:
        //       resp?.showInteractivityRequiredVisual
        //   }
        // })
        // }

        if (singleUser?.id === "all_audience") {
          setRoom(prevValue => {
            const value = Util.getUpdatedPermission(
              +prevValue.audiencePermissions,
              singleUser.permissions,
              singleUser.addPermissions,
              singleUser.removePermissions
            )
            return {
              name: "ADD",
              attributes: { audiencePermissions: value }
            }
          })

          setUsers({
            name: "UPDATE_ALL",
            attributes: {
              permissions: singleUser.permissions,
              addPermissions: singleUser.addPermissions,
              removePermissions: singleUser.removePermissions
            }
          })
          if (localUser?.type === UserType?.AUDIENCE) {
            // turn off local user device depending on permissions
            const updatedPermission = Util.getUpdatedPermission(
              localUser.permissions,
              singleUser.permissions,
              singleUser.addPermissions,
              singleUser.removePermissions
            )
            disableLocalDevices(+updatedPermission)
            // check if audience user cam and mic permission are revoked
            changeClientRoleToAudience(+updatedPermission)
          }
        } else if (singleUser?.id === "all_broadcaster") {
          setRoom(prevValue => {
            const value = Util.getUpdatedPermission(
              +prevValue.broadcasterPermissions,
              singleUser.permissions,
              singleUser.addPermissions,
              singleUser.removePermissions
            )
            return {
              name: "ADD",
              attributes: { broadcasterPermissions: value }
            }
          })
          setUsers({
            name: "UPDATE_ALL_BROADCASTER",
            attributes: {
              permissions: singleUser.permissions,
              addPermissions: singleUser.addPermissions,
              removePermissions: singleUser.removePermissions
            }
          })
          // check local user is broadcaster
          if (localUser?.type === UserType?.BROADCASTER) {
            // turn off local user device depending on permissions
            const value = Util.getUpdatedPermission(
              localUser.permissions,
              singleUser.permissions,
              singleUser.addPermissions,
              singleUser.removePermissions
            )
            disableLocalDevices(+value)
          }
        }
      }

      if (resp?.showInteractivityRequiredVisual !== undefined) {
        showInteractivityRequiredVisual(resp?.showInteractivityRequiredVisual)
        // let wpHostId = MeetOnline?.client().rtc?.getWatchPartyHostId()
        // if (
        //   wpHostId === "modNotPresentImage" ||
        //   wpHostId === "modNotPresentVideo" ||
        //   wpHostId === "modNotPresentOverlay"
        // ) {
        //   stopServerWatchParty()
        // }

        // setRoom({
        //   name: "UPDATE",
        //   attributes: {
        //     showInteractivityRequiredVisual:
        //       resp?.showInteractivityRequiredVisual
        //   }
        // })
      }

      // if (resp?.showInteractivityRequiredVisual === true) {
      //   showInteractivityRequiredVisual(true)
      // }
    } else if (resp.action === "delete") {
      // Remove users from recoil
      setUsers({ name: "REMOVE", users: resp.data })

      for (let singleUser of resp.data) {
        // Stop WatchParty if the user left is the watch party host.
        let wpHostId = MeetOnline?.client().rtc?.getWatchPartyHostId()
        if (wpHostId === singleUser?.id) {
          // Yes, watch party host suddenly left. So stop watch party.
          stopServerWatchParty()
        }

        if (resp?.showInteractivityRequiredVisual === true) {
          // let wpHostId = MeetOnline?.client().rtc?.getWatchPartyHostId()
          // if(wpHostId == ''){
          startServerWatchParty()
          // }

          setRoom({
            name: "UPDATE",
            attributes: {
              showInteractivityRequiredVisual:
                resp.showInteractivityRequiredVisual
            }
          })
        }
      }
      return
    }
  }

  const doRoomUpdate = async (response: ModelUpdatingResponse) => {
    logger.log("doRoomUpdate - ", response)
    const _activeSpeaker = await activeSpeakerCallback.handleReturnData()
    const localUser = await localUserCallbackProps.handleReturnData()

    for (let singleRoomConfig of response.data) {
      // Check if incoming message is related to Room Duration Timeout
      // durationTimerState can have 0 value so we need to check undefined
      if (
        singleRoomConfig?.durationTimerState !== undefined &&
        singleRoomConfig?.advancedSettings
      ) {
        handleRoomDurationResponse({
          durationTimerState: singleRoomConfig?.durationTimerState,
          advancedSettings: singleRoomConfig?.advancedSettings
        })
        continue
      }

      if (singleRoomConfig?.settings?.checkoutPageType !== undefined) {
        setRoom({
          name: "UPDATE",
          attributes: {
            checkoutPageType: singleRoomConfig.settings.checkoutPageType
          }
        })
      }

      if (singleRoomConfig?.settings?.enableHeaderBar !== undefined) {
        setRoom({
          name: "UPDATE",
          attributes: {
            enableHeaderBar: singleRoomConfig.settings.enableHeaderBar
          }
        })
      }

      if (singleRoomConfig?.chatInputBoxOverlay !== undefined) {
        setRoom({
          name: "UPDATE",
          attributes: {
            chatInputBoxOverlay: singleRoomConfig.chatInputBoxOverlay
          }
        })
      }

      if (singleRoomConfig?.settings?.enableShoppableVideos !== undefined) {
        setRoom({
          name: "UPDATE",
          attributes: {
            enableShoppableVideos:
              singleRoomConfig?.settings?.enableShoppableVideos
          }
        })
      }

      if (
        singleRoomConfig?.initialVideoResolutionMobilePortrait !== undefined
      ) {
        setRoom({
          name: "UPDATE",
          attributes: {
            initialVideoResolutionMobilePortrait:
              singleRoomConfig.initialVideoResolutionMobilePortrait
          }
        })
      }

      if (singleRoomConfig?.settings?.audiencePermissions !== undefined) {
        setRoom({
          name: "UPDATE",
          attributes: {
            audiencePermissions: singleRoomConfig.settings.audiencePermissions
          }
        })

        setUsers({
          name: "UPDATE_ALL",
          attributes: {
            permissions: singleRoomConfig.settings.audiencePermissions
          }
        })
      }

      if (singleRoomConfig?.settings?.broadcasterPermissions !== undefined) {
        setRoom({
          name: "UPDATE",
          attributes: {
            broadcasterPermissions:
              singleRoomConfig.settings.broadcasterPermissions
          }
        })

        setUsers({
          name: "UPDATE_ALL_BROADCASTER",
          attributes: {
            permissions: singleRoomConfig.settings.broadcasterPermissions
          }
        })
      }

      // If property in settings
      if (singleRoomConfig?.settings?.moderatorRequired !== undefined) {
        setRoom({
          name: "UPDATE",
          attributes: {
            moderatorRequired: singleRoomConfig?.settings?.moderatorRequired
          }
        })
      }

      if (singleRoomConfig?.settings?.allowChatWhenNoPresenter !== undefined) {
        setRoom({
          name: "UPDATE",
          attributes: {
            allowChatWhenNoPresenter:
              singleRoomConfig.settings.allowChatWhenNoPresenter
          }
        })
      }

      setEnableHighQualityStream(
        singleRoomConfig?.settings?.enableHighQualityStream
      )

      if (singleRoomConfig?.settings?.chatApprovalRequired !== undefined) {
        setRoom({
          name: "UPDATE",
          attributes: {
            chatApprovalRequired: singleRoomConfig.settings.chatApprovalRequired
          }
        })
      }

      if (singleRoomConfig?.settings?.enableProfanityFilter !== undefined) {
        setRoom({
          name: "UPDATE",
          attributes: {
            enableProfanityFilter:
              singleRoomConfig.settings.enableProfanityFilter
          }
        })
      }

      // If property is outside settings - when mod change property from control side panel
      if (singleRoomConfig?.moderatorRequired !== undefined) {
        setRoom({
          name: "UPDATE",
          attributes: {
            moderatorRequired: singleRoomConfig.moderatorRequired
          }
        })
      }

      if (singleRoomConfig?.allowChatWhenNoPresenter !== undefined) {
        setRoom({
          name: "UPDATE",
          attributes: {
            allowChatWhenNoPresenter: singleRoomConfig.allowChatWhenNoPresenter
          }
        })
      }

      if (singleRoomConfig?.chatApprovalRequired !== undefined) {
        setRoom({
          name: "UPDATE",
          attributes: {
            chatApprovalRequired: singleRoomConfig.chatApprovalRequired
          }
        })
      }

      if (singleRoomConfig?.enableProfanityFilter !== undefined) {
        setRoom({
          name: "UPDATE",
          attributes: {
            enableProfanityFilter: singleRoomConfig.enableProfanityFilter
          }
        })
      }

      setRtmpStreamEnable(singleRoomConfig?.rtmpStreamEnable)
      setAudienceBuffering(singleRoomConfig?.audienceBuffering)
      setStreamingResolution(singleRoomConfig?.streamingResolution)

      if (singleRoomConfig?.showInteractivityRequiredVisual !== undefined) {
        showInteractivityRequiredVisual(
          singleRoomConfig?.showInteractivityRequiredVisual
        )
        // // eslint-disable-next-line @typescript-eslint/no-unused-vars
        // let wpHostId = MeetOnline?.client().rtc?.getWatchPartyHostId()
        // // if (wpHostId == '' || wpHostId == "modNotPresentImage" || wpHostId == "modNotPresentVideo" || wpHostId == "modNotPresentOverlay") {
        // startServerWatchParty()
        // // }

        // setRoom({
        //   name: "UPDATE",
        //   attributes: {
        //     showInteractivityRequiredVisual:
        //       singleRoomConfig.showInteractivityRequiredVisual
        //   }
        // })
      }

      if (singleRoomConfig?.watchPartyInProgress === true) {
        // Watch party has started, Need to stop screen-share if enabled
        if (localUser?.screenShareEnabled === true) {
          MeetOnline?.client().rtc.requestScreenShare()
        }
      }

      console.log("single room config", singleRoomConfig)


      if (singleRoomConfig?.activeSpeakerUid !== undefined) {
        logger.log("inside-vad-check", singleRoomConfig.activeSpeakerUid)
        // insert last speak time here
        setUsers({
          name: "UPDATE",
          userId: singleRoomConfig.activeSpeakerUid,
          attributes: {
            lastSpeakTime: new Date().getTime()
          }
        })

        // Commented this condition to check if current user is active speaker
        // if (singleRoomConfig?.activeSpeakerUid !== _localUserId) {
        if (singleRoomConfig?.activeSpeakerUid !== _activeSpeaker.uid) {
          setActiveSpeaker({
            uid: singleRoomConfig.activeSpeakerUid
          })
        }
        // }
      }

      if (singleRoomConfig?.heartAnimating !== undefined) {
        setHeartAnimation(singleRoomConfig.heartAnimating)
      }
    }
  }

  const doChatUpdate = async (response: ModelUpdatingResponse) => {
    const _allChatIds = await chatCallbackProps.handleReturnData()
    const replyIds = await replyIdsCallbackProps.handleReturnData()
    const localUser = await localUserCallbackProps.handleReturnData()
    const isFirstTimeChatResponse =
      await isFirstTimeChatResponseProps.handleReturnData()
    
    // ADD DATA
    if (response?.action === undefined) {
      setChats({
        type: "ADD_ALL",
        payload: { data: response.data }
      })
    }

    // REACTIONS
    if (response?.action === "reaction") {
      const data = response.data[0] as AddReactionsData

      if (data?.parentChatId !== undefined) {
        setChats({
          type: "ADD_REACTION_REPLY",
          payload: {
            id: data.id,
            attributes: {
              reactions: data.reactions,
              parentChatId: data?.parentChatId
            }
          }
        })
        setLastReactionsId({
          type: "reply",
          id: data.id,
          parentChatId: data?.parentChatId
        })
      }

      if (data?.parentChatId === undefined) {
        setChats({
          type: "UPDATE",
          payload: {
            id: data.id,
            attributes: { reactions: data.reactions }
          }
        })
        setLastReactionsId({ type: "message", id: data.id })
      }
    }

    // DELETE
    if (response.action === "delete") {
      const data = response.data[0]

      //Remove all messages
      if (data.delete === "all") {
        setChats({ type: "REMOVE_ALL" })
        setIsClearChat(true)
        setChatIsRead(true)
      }

      //Remove one message or one reply
      if (data.delete !== "all") {
        setChats({
          type: "REMOVE",
          payload: { id: data.delete }
        })
      }

      //Remove ID`s replys if message had them
      if (data?.replyChatIds !== undefined && data?.replyChatIds.length > 0) {
        setChats({
          type: "REMOVE_REPLYS_IDS",
          payload: { replysIds: data?.replyChatIds }
        })
      }
    }

    // UPDATE
    if (response.action === "update") {
      const data = response.data[0]

      // If we are moderator or we are author
      if (
        localUser?.type === UserType.MODERATOR ||
        localUser?.id === data?.author?.id
      ) {
        if (
          data.chatStatus === ChatStatus.APPROVED ||
          data.chatStatus === ChatStatus.REJECTED
        ) {
          if (
            data.parentChatId !== undefined &&
            replyIds.some(el => el.id === data.id)
          ) {
            setChats({
              type: "UPDATE",
              payload: {
                id: data.id,
                attributes: { chatStatus: data.chatStatus }
              }
            })
          }

          //If we have same message, we update props
          else if (_allChatIds.indexOf(data.id) > -1) {
            setChats({
              type: "UPDATE",
              payload: {
                id: data.id,
                attributes: {
                  chatStatus: data.chatStatus,
                  replyChats: data.replyChats
                }
              }
            })
          } else {
            //If we dont have same message, we are create new message
            setChats({
              type: "ADD",
              payload: { message: data }
            })
          }

          //Just add message if have this statuses in bottom
        } else if (
          data?.chatStatus === ChatStatus.AUTO_APPROVED ||
          data?.chatStatus === ChatStatus.AUTO_REJECTED ||
          data?.chatStatus === ChatStatus.NEW
        ) {
          setChats({
            type: "ADD",
            payload: { message: data }
          })
        }
      } else {
        if (data?.chatStatus === ChatStatus.APPROVED) {
          if (_allChatIds.indexOf(data.id) > -1) {
            setChats({
              type: "UPDATE",
              payload: {
                id: data.id,
                attributes: {
                  chatStatus: data.chatStatus,
                  replyChats: data.replyChats
                }
              }
            })
          } else {
            setChats({
              type: "ADD",
              payload: { message: data }
            })
          }
        } else if (data.chatStatus === ChatStatus.REJECTED) {
          // if local user is not an author of main message and mod REJECTED/UNAPPROVED the chat, it will get deleted for that user

          setChats({
            type: "REMOVE",
            payload: { id: data?.id }
          })

          if (
            data?.replyChatIds !== undefined &&
            data?.replyChatIds.length > 0
          ) {
            setChats({
              type: "REMOVE_REPLYS_IDS",
              payload: { replysIds: data?.replyChatIds }
            })
          }
        }
      }
    }
  }

  const currentOverlayRef = useRef(null)

  const doOverlayUpdate = async (response: ModelUpdatingResponse) => {
    // Clear current overlay when action is undefined, mean create message
    currentOverlayRef.current = response?.action && currentOverlayRef.current
    const _overlay = currentOverlayRef.current

    let productMap = {}
    if (response?.action !== "delete") {
      // Extract the productIds from response
      const productIds = response.data
        .filter(o => !!o.props && !o.productDetails)
        .map(o => o.props)

      // Response contain product data, no need to fetch from the server if productDetails present
      const responseContainData = response.data
        .filter(o => !!o.props && o.productDetails)
        .map(o => o.productDetails)

      if (productIds.length !== 0) {
        // Fetch all the product details before hand.
        const products = await MeetOnline?.client().fetchProductByIds(
          productIds
        )
        productMap = products.reduce((acc, cur) => {
          return { ...acc, [cur.id]: cur }
        }, {})
      } else if (responseContainData.length !== 0) {
        // productDetail is present but with minimized key
        const products =
          MeetOnline?.client().extractProductDetail(responseContainData)

        productMap = products.reduce((acc, cur) => {
          return { ...acc, [cur.id]: cur }
        }, productMap)
      }
    }

    if(!response?.action) {
      setBadgeHistory({ name: "RESET" })
      setOverlay(null)
    }

    for (let singleOverlay of response.data) {
      // merge the props data into one object
      if (singleOverlay.props && singleOverlay.props instanceof Object) {
        singleOverlay = {
          ...singleOverlay.props,
          ...singleOverlay
        }

        delete singleOverlay.props
      }

      if (singleOverlay?.type === "badge") {
        // unpublished or delete product detail will be not present in product Map
        const isRemoveOverlayReq =
          singleOverlay?.id === singleOverlay?.unpublishedId ||
          response?.action === "delete"
        if (!isRemoveOverlayReq && !productMap[singleOverlay.id]) {
          logger.error("Product not found", singleOverlay)
          // if product fail to fetch, then ignore that product
          continue
        }

        // merge the products data into one object
        singleOverlay = {
          ...singleOverlay,
          ...productMap[singleOverlay.id]
        }
      }

      // Hide active overlay if unpublished and delete id match current active overlay
      if (
        _overlay?.id &&
        (_overlay?.id === singleOverlay?.unpublishedId ||
          _overlay?.id === singleOverlay?.delete)
      ) {
        if (
          _overlay !== null &&
          _overlay !== undefined &&
          _overlay.type === "chat"
        ) {
          if (_overlay.parentChatId !== undefined) {
            setChats({
              type: "UPDATE_REPLY",
              payload: {
                id: _overlay.id,
                parentChatId: _overlay.parentChatId,
                attributes: { published: false }
              }
            })
          } else {
            setChats({
              type: "UPDATE",
              payload: {
                id: _overlay.id,
                attributes: { published: false }
              }
            })
          }
        }

        if (
          _overlay !== null &&
          _overlay !== undefined &&
          _overlay.type === "badge"
        ) {
          setBadges({
            name: "UPDATE",
            id: _overlay.id,
            attributes: { published: false }
          })
        }

        // Remove display overlay if it unpublished or deleted
        setOverlay({ ...singleOverlay, published: false })
        currentOverlayRef.current = null
      }

      // if type chat: Publish chat on the mainWindowScreen
      if (singleOverlay?.type === "chat" && singleOverlay?.published === true) {
        setOverlay(singleOverlay)
        currentOverlayRef.current = singleOverlay
        setEnabledOverlay(true)
      }

      // if type badge: Publish badge on the mainWindowScreen
      if (
        singleOverlay?.type === "badge" &&
        singleOverlay?.published === true
      ) {
        setOverlay(singleOverlay)
        currentOverlayRef.current = singleOverlay
        setEnabledOverlay(true)
        // setPiPParams({ coverImage: singleOverlay!.coverImage })
      }

      // if overlay delete: delete overlay in history list
      if (singleOverlay.delete) {
        if (singleOverlay.delete === "all") {
          // if any badges is published
          if (_overlay?.type === "badge") {
            setBadges({
              name: "UPDATE",
              id: _overlay.id,
              attributes: { published: false }
            })
            setOverlay({
              ..._overlay,
              published: false,
              unpublishedId: _overlay.id
            })
            currentOverlayRef.current = null
          }
          setBadgeHistory({ name: "RESET" })
        } else {
          setBadgeHistory({
            name: "DELETE",
            badgeHistoryId: singleOverlay.delete
          })
        }
      }

      // Add the product in history list
      if (singleOverlay.type === "badge" && !singleOverlay.delete) {
        if (singleOverlay?.title !== "" && singleOverlay?.title !== undefined) {
          const { enabledShoppableMNP, idShoppableMNP } =
            await shoppableMNPVideoProps.handleReturnData()
          const shoppableVideosList =
            await shoppableVideosListProps.handleReturnData()

          if (enabledShoppableMNP) {
            const currentVideoBadge = shoppableVideosList.find(
              obj => obj.id === idShoppableMNP
            )

            const productsData = await MeetOnline?.client().fetchProductByIds(
              currentVideoBadge.products
            )

            setTaggedBadges(productsData)
          }
          setBadgeHistory({
            name: "ADD",
            badgeHistoryId: singleOverlay?.id,
            badgeHistoryData: singleOverlay
          })
        }
      }
    }
  }

  const doPollUpdate = async (response: ModelUpdatingResponse) => {
    const pollsList = await pollsCallbackProps.handleReturnData()
    
    if(response?.action === undefined) {
      // clear all previous polls
      setPolls({ type: "RESET" })
    }

    for (let singlePoll of response.data) {
      if (response?.action === "delete") {
        setPolls({
          type: "DELETE_POLL",
          payload: singlePoll
        })
      }

      if (response?.action === undefined || response?.action === "create") {
        const options = Object.entries(sortPollOptions(singlePoll.options)).map(
          el => ({
            choice: parsePollOption(el[0]),
            vote: el[1]
          })
        )

        const data = {
          id: singlePoll.id,
          title: singlePoll.question,
          // isVisible: true,
          isVisible: singlePoll.visible,
          totalResponses: singlePoll.totalResponses,
          timestamp: singlePoll.timestamp,
          choices: options,
          author: singlePoll.author,
          isVisibleMobile: true
        }
        setPolls({ type: "CREATE_POLL", payload: data })
      }

      if (response?.action === "update") {
        const isNoSuchPoll =
          pollsList.filter(poll => poll.id === singlePoll.id).length === 0
        const options = Object.entries(sortPollOptions(singlePoll.options)).map(
          el => ({
            choice: parsePollOption(el[0]),
            vote: el[1]
          })
        )
        if (isNoSuchPoll) {
          const data = {
            id: singlePoll.id,
            title: singlePoll.question,

            isVisible: singlePoll.visible,
            totalResponses: singlePoll.totalResponses,
            timestamp: singlePoll.timestamp,
            choices: options,
            author: singlePoll.author,
            isVisibleMobile: true
          }
          setPolls({ type: "CREATE_POLL", payload: data })
        } else {
          const data = {
            id: singlePoll.id,
            totalResponses: singlePoll.totalResponses,
            choices: options,
            isVisible: singlePoll.visible
          }
          setPolls({ type: "UPDATE", payload: data })
        }
      }

      if (response?.action === "vote") {
        const options = Object.entries(sortPollOptions(singlePoll.options)).map(
          el => ({
            choice: parsePollOption(el[0]),
            vote: el[1]
          })
        )

        const data = {
          id: singlePoll.id,
          totalResponses: singlePoll.totalResponses,
          choices: options,
          isVisible: singlePoll.visible
        }
        setPolls({ type: "UPDATE", payload: data })
      }
    }
  }

  const doStreamConfigUpdate = (response: ModelUpdatingResponse) => {
    logger.log("handleModelUpdate-doStreamConfigUpdate", response)

    if (response?.action === undefined || response?.action === "create") {
      setStreamsList(
        middlewareStream({ type: "DOWNLOAD", payload: response.data })
      )
    } else {
      for (let singleStreamConfig of response.data) {
        if (response?.action === "update") {
          setStreamsList(
            middlewareStream({
              type: "UPDATE",
              payload: {
                id: singleStreamConfig.id,
                label: singleStreamConfig.label,
                rtmpUrl: singleStreamConfig.rtmpUrl
              }
            })
          )
        }

        if (response?.action === "delete") {
          setStreamsList(
            middlewareStream({
              type: "DELETE",
              payload: { id: singleStreamConfig.delete }
            })
          )
        }
      }
    }
  }

  const doAuctionUpdate = (response: ModelUpdatingResponse) => {
    logger.log("handleModelUpdate-doAuctionUpdate", response)

    // reset the auction, if data is empty
    if(response.data?.length === 0) {
      setAuctionParams({ action: "reset" })
      return
    }

    // Exit the PIP
    if (document.pictureInPictureElement) {
      document.exitPictureInPicture()
      setPiPParams({ isActivePiP: false })
    }
    
    const auction = response.data[0] // As only one auction can be active

    if (response?.action === "create" || response?.action === undefined) {
      if (auction?.ask === undefined && auction.startingPrice) {
        auction.ask = auction.startingPrice
      }
      setAuctionParams(auction)
    }

    if (response?.action === "start") {
      setAuctionParams(auction)
    }

    if (response?.action === "createAndStart") {
      setAuctionFinalResult({
        type: "UPDATE",
        payload: { isFinalModal: false }
      })
      setAuctionParams(auction)
    }

    if (response?.action === "stop") {
      setAuctionParams(auction)
    }

    if (response?.action === "ask") {
      setAuctionParams(auction)
    }

    if (response?.action === "bid") {
      const actionProps = {
        ...auction,
        action: "bid",
        lastOnetoBid: auction.biddingUser.id
      }
      setAuctionParams(actionProps)
    }

    if (response.action === "update") {
      setAuctionParams(auction)
    }

    if (response?.action === "delete") {
      setAuctionFinalResult({ type: "SHOW_MODAL" })
      setAuctionParams({ action: "reset" })
    }
  }

  const doWatchPartyConfigUpdate = (response: ModelUpdatingResponse) => {
    logger.log("handleModelUpdate-doWatchPartyConfigUpdate", response)
    if (response?.action === undefined || response?.action === "create") {
      for (let singleWpConfig of response.data) {
        setWatchPartyHistoryLinks({
          type: "ADD",
          payload: {
            id: singleWpConfig.id,
            url: singleWpConfig.url
          }
        })
      }
    }

    if (response?.action === "delete") {
      for (let singleWpConfig of response.data) {
        if (singleWpConfig?.delete) {
          setWatchPartyHistoryLinks({
            type: "DELETE",
            payload: {
              id: singleWpConfig.delete
            }
          })
        }
      }
    }
  }

  const doProductsUpdate = async (response: ModelUpdatingResponse) => {
    if (!response.data || response.data.length === 0) return // data is undefine or empty

    if (response.action === "create" || response.action === undefined) {
      // fetch product from server
      const products = await MeetOnline?.client().fetchProductByIds(
        response.data
      )
      if (products.length !== 0) {
        setBadges({ name: "ADD", badges: products })
      }
    } else {
      // Extract product ids from the response
      const productIds = response.data
        .filter(p => p.action !== "delete")
        .map(p => p.product)
      let productMap = {}
      if (productIds.length !== 0) {
        // Fetch all the products
        const products = await MeetOnline?.client().fetchProductByIds(
          productIds
        )
        // Map the all product by id. because it will be easy to fetch
        productMap = products.reduce((acc, cur) => {
          return { ...acc, [cur.id]: cur }
        }, {})
      }

      const newProducts = []
      const updatedProducts = []
      const deleteProducts = response.data
        .filter(p => p.action === "delete")
        .map(p => p.product)

      for (const data of response.data) {
        if (data.action === "create") {
          if (productMap[data.product.id])
            newProducts.push(productMap[data.product.id])
        } else if (data.action === "update") {
          const { enabledShoppableMNP, idShoppableMNP } =
            await shoppableMNPVideoProps.handleReturnData()
          const shoppableVideosList =
            await shoppableVideosListProps.handleReturnData()

          if (enabledShoppableMNP) {
            const currentVideoBadge = shoppableVideosList.find(
              obj => obj.id === idShoppableMNP
            )

            const productsData = await MeetOnline?.client().fetchProductByIds(
              currentVideoBadge.products
            )

            setTaggedBadges(productsData)
          }

          if (productMap[data.product.id])
            updatedProducts.push(productMap[data.product.id])
        }
      }

      if (newProducts.length !== 0) {
        setBadges({ name: "ADD", badges: newProducts })
      }

      if (updatedProducts.length) {
        setBadges({ name: "UPDATE_MULTI", badges: updatedProducts })
      }

      if (deleteProducts.length !== 0) {
        setBadges({ name: "REMOVE", badges: deleteProducts })
      }
    }
  }

  const doMediaUpdate = async response => {
    const { enabledShoppableMNP, idShoppableMNP, typeShoppableMNP } =
      await shoppableMNPVideoProps.handleReturnData()
    const localUser = await localUserCallbackProps.handleReturnData()
    const {
      shoppableVideosAfterPictureNoPresentHostId,
      isShoppableVideosAfterPictureNoPresent
    } = await shoppableVideosAfterPictureNoPresentProps.handleReturnData()
    if (response.action === "update") {
      for (let shoppableVideos of response.data) {
        if (shoppableVideos?.action === "update") {
          // eslint-disable-next-line eqeqeq
          if (enabledShoppableMNP && idShoppableMNP == shoppableVideos.id) {
            const productsData = await MeetOnline?.client().fetchProductByIds(
              shoppableVideos.products
            )

            setTaggedBadges(productsData)
          }
          setShoppableVideos({
            name: "UPDATE",
            shoppableVideoData: shoppableVideos
          })
        }

        if (shoppableVideos?.action === "create") {
          setShoppableVideos({
            name: "ADD",
            shoppableVideoData: shoppableVideos
          })
        }

        if (shoppableVideos?.action === "delete") {
          // eslint-disable-next-line eqeqeq
          if (idShoppableMNP == shoppableVideos?.id) {
            if (isShoppableVideosAfterPictureNoPresent) {
              setWatchPartyHostId(shoppableVideosAfterPictureNoPresentHostId)
              setShoppableVideosAfterPictureNoPresent({
                isShoppableVideosAfterPictureNoPresent: false,
                shoppableVideosAfterPictureNoPresentHostId: ""
              })
            }

            if (typeShoppableMNP === "share") {
              await MeetOnline?.client().rtc?.stopWatchParty()

              await MeetOnline?.client()?.sendStoppingWatchParty(localUser?.id)
            }
            setTaggedBadges([])
            setShoppableMNPVideo({
              srcShoppableMNP: "",
              enabledShoppableMNP: false,
              idShoppableMNP: "",
              typeShoppableMNP: ""
            })
          }

          setShoppableVideos({
            name: "DELETE",
            shoppableVideoId: shoppableVideos.id
          })
        }
      }
    } else {
      for (let shoppableVideos of response.data) {
        setShoppableVideos({ name: "ADD", shoppableVideoData: shoppableVideos })
      }
    }
  }

  return {
    doUserUpdate,
    doAuctionUpdate,
    doChatUpdate,
    doOverlayUpdate,
    doPollUpdate,
    doRoomUpdate,
    doProductsUpdate,
    doStreamConfigUpdate,
    doWatchPartyConfigUpdate,
    doMediaUpdate
  }
}
